import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSlidesViewerNavigationPrefetchData } from 'api/slides';
import { compact, uniq } from 'es-toolkit/compat';
import { SlidePrefetchData } from 'interfaces/slide';
import React from 'react';
import { ArrayParam, BooleanParam, JsonParam, StringParam } from 'use-query-params';
import useTaxonomy, { cellTaxonomy } from 'utils/queryHooks/taxonomy/useTaxonomy';
import {
  collectEntityIdsFromMergeTags,
  EntityMergeTagContext,
  EntityMergeTagLookups,
  resolveEntityMergeTags,
} from 'utils/resolveEntityMergeTags';
import { resolveOriginTemplate } from 'utils/resolveOriginTemplate';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { encodeQueryParamsUsingSchema } from 'utils/useEncodedFilters/helpers';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';

const ENTITY_MERGE_TAG_SLIDE_QUERY_STALE_TIME_MS = 5 * 60 * 1000;

const entityMergeTagSlidePrefetchSchema = {
  filters: JsonParam,
  labId: StringParam,
  slideIdsToInclude: ArrayParam,
  skipPagination: BooleanParam,
};

const collectCachedSlidePrefetchData = ({
  queryClient,
  slideIds,
}: {
  queryClient: ReturnType<typeof useQueryClient>;
  slideIds: string[];
}) => {
  const slideIdsSet = new Set(slideIds);
  const cachedSlidesById: Record<string, SlidePrefetchData> = {};

  const cachedQueryPrefixes = [
    ['slidesViewerNavigationPrefetchData'],
    ['jobResultSlidesViewerNavigationPrefetchData'],
    ['studyReportSlidesViewerNavigationPrefetchData'],
    ['entityMergeTagSlidesViewerNavigationPrefetchData'],
  ];

  cachedQueryPrefixes.forEach((queryKey) => {
    for (const [, cachedSlideBatch] of queryClient.getQueriesData<SlidePrefetchData[]>({ queryKey })) {
      if (!Array.isArray(cachedSlideBatch)) {
        continue;
      }

      for (const cachedSlide of cachedSlideBatch) {
        if (slideIdsSet.has(cachedSlide.slideId) && !cachedSlidesById[cachedSlide.slideId]) {
          cachedSlidesById[cachedSlide.slideId] = cachedSlide;
        }
      }
    }
  });

  return cachedSlidesById;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const useEntityMergeTagResolver = ({ content, studyId }: { content: string; studyId?: string }): string => {
  const queryClient = useQueryClient();
  const { labId } = useCurrentLabId();
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();
  const { idToDisplayNameMap: cellTypeLabelById } = useTaxonomy(cellTaxonomy);

  const entityIds = React.useMemo(() => collectEntityIdsFromMergeTags(content), [content]);

  const slideIdsInScope = React.useMemo(
    () => uniq(compact(entityIds.slideIds)).filter((id) => UUID_PATTERN.test(id)),
    [entityIds.slideIds]
  );
  const markerIdsInScope = React.useMemo(() => uniq(compact(entityIds.markerIds)), [entityIds.markerIds]);

  const hasEntityMergeTags =
    slideIdsInScope.length > 0 || markerIdsInScope.length > 0 || entityIds.cellTypeIds.length > 0;

  const cachedSlidesById = React.useMemo(
    () =>
      slideIdsInScope.length > 0 ? collectCachedSlidePrefetchData({ queryClient, slideIds: slideIdsInScope }) : {},
    [queryClient, slideIdsInScope]
  );
  const missingSlideIds = React.useMemo(
    () => slideIdsInScope.filter((slideId) => !cachedSlidesById[slideId]),
    [cachedSlidesById, slideIdsInScope]
  );

  const slidePrefetchQueryParams = React.useMemo(() => {
    if (!studyId || !labId || !missingSlideIds.length) {
      return null;
    }

    return encodeQueryParamsUsingSchema(
      {
        filters: { studyId },
        labId,
        slideIdsToInclude: missingSlideIds,
        skipPagination: true,
      },
      entityMergeTagSlidePrefetchSchema,
      { arrayFormat: 'repeat' }
    );
  }, [labId, missingSlideIds, studyId]);

  const { data: queriedSlides = [] } = useQuery({
    queryKey: ['entityMergeTagSlidesViewerNavigationPrefetchData', slidePrefetchQueryParams],
    queryFn: ({ signal }) => getSlidesViewerNavigationPrefetchData(slidePrefetchQueryParams || '', signal),
    enabled: Boolean(slidePrefetchQueryParams),
    staleTime: ENTITY_MERGE_TAG_SLIDE_QUERY_STALE_TIME_MS,
  });

  const slidesById = React.useMemo(
    () =>
      [...Object.values(cachedSlidesById), ...queriedSlides].reduce<Record<string, SlidePrefetchData>>(
        (currentSlidesById, slide) => {
          currentSlidesById[slide.slideId] = slide;
          return currentSlidesById;
        },
        {}
      ),
    [cachedSlidesById, queriedSlides]
  );

  const markerLabelById = React.useMemo(
    () =>
      markerIdsInScope.reduce<Record<string, string>>((currentMarkerLabelById, markerId) => {
        currentMarkerLabelById[markerId] = stainTypeIdToDisplayName(markerId) || markerId;
        return currentMarkerLabelById;
      }, {}),
    [markerIdsInScope, stainTypeIdToDisplayName]
  );

  const lookups = React.useMemo<EntityMergeTagLookups>(
    () => ({
      slideById: slidesById,
      markerLabelById,
      cellTypeLabelById,
    }),
    [cellTypeLabelById, markerLabelById, slidesById]
  );

  const context = React.useMemo<EntityMergeTagContext>(() => ({ studyId, labId }), [labId, studyId]);

  return React.useMemo(() => {
    if (!hasEntityMergeTags) {
      return resolveOriginTemplate(content);
    }

    return resolveOriginTemplate(resolveEntityMergeTags(content, lookups, context));
  }, [content, context, hasEntityMergeTags, lookups]);
};
